import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Crown, ArrowLeft, Target, Zap, Medal } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }),
};

const RANK_STYLES = [
  { bg: "bg-gradient-to-r from-yellow-50 to-amber-50", border: "border-yellow-200", icon: Crown, iconColor: "text-yellow-500" },
  { bg: "bg-gradient-to-r from-gray-50 to-slate-50", border: "border-gray-200", icon: Medal, iconColor: "text-gray-400" },
  { bg: "bg-gradient-to-r from-orange-50 to-amber-50", border: "border-orange-200", icon: Medal, iconColor: "text-amber-600" },
];

export default function ChampionsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [champions, setChampions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/challenges/champions/leaderboard")
      .then((res) => setChampions(res.data.champions || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) {
    return (
      <div className="py-6 space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-foreground/5 rounded-3xl h-16 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="py-6 space-y-5" data-testid="champions-page">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#AFA8BA] transition-colors"
        data-testid="champions-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#FF6B35]/15 via-[#7000FF]/10 to-transparent border border-[#FF6B35]/20 rounded-3xl p-6 text-center"
      >
        <div className="w-14 h-14 rounded-2xl bg-[#FF6B35]/15 flex items-center justify-center mx-auto mb-3">
          <Trophy className="w-7 h-7 text-[#FF6B35]" />
        </div>
        <h1 className="font-heading font-extrabold text-2xl text-foreground">Champions des Defis</h1>
        <p className="text-sm text-muted-foreground mt-1">Les fans les plus actifs dans les defis</p>
      </motion.div>

      {/* Champions List */}
      {champions.length === 0 ? (
        <div className="text-center py-12">
          <Target className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Aucun champion pour le moment</p>
          <p className="text-xs text-muted-foreground mt-1">Participe a des defis pour apparaitre ici !</p>
          <button
            onClick={() => navigate("/challenges")}
            className="mt-4 bg-[#FF6B35] text-white font-bold px-6 py-2.5 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform"
            data-testid="go-to-challenges-btn"
          >
            Voir les defis
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {champions.map((champ, idx) => {
            const rankStyle = RANK_STYLES[idx] || null;
            return (
              <motion.div
                key={champ.user_id}
                custom={idx}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                className={`flex items-center gap-3 p-4 rounded-2xl border transition-all hover:scale-[1.01] ${
                  rankStyle
                    ? `${rankStyle.bg} ${rankStyle.border}`
                    : "bg-card border-border"
                }`}
                data-testid={`champion-${champ.user_id}`}
              >
                <div className="w-8 text-center shrink-0">
                  {rankStyle ? (
                    <rankStyle.icon className={`w-5 h-5 mx-auto ${rankStyle.iconColor}`} />
                  ) : (
                    <span className="font-mono-credits font-bold text-sm text-muted-foreground">{idx + 1}</span>
                  )}
                </div>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center shrink-0 overflow-hidden">
                  {champ.avatar_url ? (
                    <img src={champ.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-bold text-lg">
                      {(champ.pseudo || "?")[0].toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/user/${champ.user_id}`} className="font-heading font-bold text-sm text-card-foreground hover:text-[#FF6B35] transition-colors">{champ.pseudo}</Link>
                  <div className="flex items-center gap-1 mt-0.5">
                    <Zap className="w-3 h-3 text-[#FF6B35]" />
                    <span className="text-[10px] text-muted-foreground">
                      {champ.completed_count} defi{champ.completed_count > 1 ? "s" : ""} complete{champ.completed_count > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
                <div className="bg-[#FF6B35]/10 rounded-full px-3 py-1.5">
                  <span className="font-mono-credits font-bold text-sm text-[#FF6B35]">
                    #{idx + 1}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
